import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { LucideIconsModule } from '../../../shared/lucide-icons/lucide-icons.module';
import { Subscription, combineLatest, startWith } from 'rxjs';
import { CommonService } from '../../../services/common.service';
import { UserAccessService } from '../../../services/user-access.service';
import {
  ALL_SIDE_MENU_SECTIONS,
  SideMenuItem,
  SideMenuSection,
  cloneSideMenuSections,
  filterSideMenuSections,
  isMenuItemActive,
} from '../../../utils/side-menu.config';
import { allowedPathsForRole } from '../../../utils/rbac-routes.util';

export type { SideMenuItem, SideMenuSection };

@Component({
  selector: 'app-sidemenu',
  standalone: true,
  imports: [NgClass, RouterLink, NzToolTipModule, LucideIconsModule],
  templateUrl: './sidemenu.component.html',
  styleUrl: './sidemenu.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidemenuComponent implements OnInit, OnDestroy {
  @Input() activeRoute: string = '';
  @Input() expanded: boolean = true;
  @Output() toggleMenu = new EventEmitter<void>();

  private commonService = inject(CommonService);
  private userAccess = inject(UserAccessService);
  private cdr = inject(ChangeDetectorRef);
  private menuSub?: Subscription;

  private readonly allSideMenuSections = ALL_SIDE_MENU_SECTIONS;

  sideMenuSections: SideMenuSection[] = cloneSideMenuSections(this.allSideMenuSections);
  private menuPathsCache: string[] = [];

  /** Flat list of menu items (used by tests and legacy consumers). */
  get sideMenu(): SideMenuItem[] {
    return this.sideMenuSections.flatMap((section) => section.items);
  }

  isItemActive(itemPath: string): boolean {
    return isMenuItemActive(itemPath, this.activeRoute, this.menuPathsCache);
  }

  ngOnInit(): void {
    this.menuPathsCache = this.sideMenu.map((item) => item.path);
    this.menuSub = combineLatest([
      this.commonService.selectedOutlet.pipe(startWith(null)),
      this.userAccess.fetchAccess(),
    ]).subscribe(([outlet, access]) => {
      this.sideMenuSections = filterSideMenuSections(
        this.allSideMenuSections,
        (outlet as any)?.posName,
        allowedPathsForRole(access.role),
        access.role,
      );
      this.menuPathsCache = this.sideMenu.map((item) => item.path);
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.menuSub?.unsubscribe();
  }

  toggleSidebar() {
    this.toggleMenu.emit();
  }
}
